import { Subject, } from "rxjs";
import { publishReplay, refCount, startWith } from "rxjs/operators";
import { ImageSize } from "../viewer/ImageSize";
/**
 * @class NodeCache
 *
 * @classdesc Represents the cached properties of a node.
 */
export class NodeCache {
    /**
     * Create a new node cache instance.
     */
    constructor(provider) {
        this._disposed = false;
        this._provider = provider;
        this._image = null;
        this._sequenceEdges = { cached: false, edges: [] };
        this._spatialEdges = { cached: false, edges: [] };
        this._imageChanged$ = new Subject();
        this._image$ = this._imageChanged$.pipe(startWith(null), publishReplay(1), refCount());
        this._iamgeSubscription = this._image$.subscribe();
        this._sequenceEdgesChanged$ = new Subject();
        this._sequenceEdges$ = this._sequenceEdgesChanged$.pipe(startWith(this._sequenceEdges), publishReplay(1), refCount());
        this._sequenceEdgesSubscription = this._sequenceEdges$.subscribe(() => {
            /*noop*/
        });
        this._spatialEdgesChanged$ = new Subject();
        this._spatialEdges$ = this._spatialEdgesChanged$.pipe(startWith(this._spatialEdges), publishReplay(1), refCount());
        this._spatialEdgesSubscription = this._spatialEdges$.subscribe(() => {
            /*noop*/
        });
        this._cachingAssets$ = null;
    }
    /**
     * Get image.
     *
     * @description Will not be set when assets have not been cached
     * or when the object has been disposed.
     *
     * @returns {HTMLImageElement} Cached image element of the node.
     */
    get image() {
        return this._image;
    }
    /**
     * Get image$.
     *
     * @returns {Observable<HTMLImageElement>} Observable emitting
     * the cached image when it is updated.
     */
    get image$() {
        return this._image$;
    }
    /**
     * Get sequenceEdges.
     *
     * @returns {IEdgeStatus} Value describing the status of the
     * sequence edges.
     */
    get sequenceEdges() {
        return this._sequenceEdges;
    }
    /**
     * Get sequenceEdges$.
     *
     * @returns {Observable<IEdgeStatus>} Observable emitting
     * values describing the status of the sequence edges.
     */
    get sequenceEdges$() {
        return this._sequenceEdges$;
    }
    /**
     * Get spatialEdges.
     *
     * @returns {IEdgeStatus} Value describing the status of the
     * spatial edges.
     */
    get spatialEdges() {
        return this._spatialEdges;
    }
    /**
     * Get spatialEdges$.
     *
     * @returns {Observable<IEdgeStatus>} Observable emitting
     * values describing the status of the spatial edges.
     */
    get spatialEdges$() {
        return this._spatialEdges$;
    }
    /**
     * Cache the sequence edges.
     *
     * @param {Array<IEdge>} edges - Sequence edges to cache.
     */
    cacheSequenceEdges(edges) {
        this._sequenceEdges = { cached: true, edges: edges };
        this._sequenceEdgesChanged$.next(this._sequenceEdges);
    }
    /**
     * Cache the spatial edges.
     *
     * @param {Array<IEdge>} edges - Spatial edges to cache.
     */
    cacheSpatialEdges(edges) {
        this._spatialEdges = { cached: true, edges: edges };
        this._spatialEdgesChanged$.next(this._spatialEdges);
    }
    /**
     * Dispose the node cache.
     *
     * @description Disposes all cached assets and unsubscribes to
     * all streams.
     */
    dispose() {
        this._iamgeSubscription.unsubscribe();
        this._sequenceEdgesSubscription.unsubscribe();
        this._spatialEdgesSubscription.unsubscribe();
        this._disposeImage();
        this._sequenceEdges = { cached: false, edges: [] };
        this._spatialEdges = { cached: false, edges: [] };
        this._imageChanged$.next(null);
        this._sequenceEdgesChanged$.next(this._sequenceEdges);
        this._spatialEdgesChanged$.next(this._spatialEdges);
        this._disposed = true;
        if (this._imageAborter != null) {
            this._imageAborter();
            this._imageAborter = null;
        }
        if (this._meshAborter != null) {
            this._meshAborter();
            this._meshAborter = null;
        }
    }
    /**
     * Reset the sequence edges.
     */
    resetSequenceEdges() {
        this._sequenceEdges = { cached: false, edges: [] };
        this._sequenceEdgesChanged$.next(this._sequenceEdges);
    }
    /**
     * Reset the spatial edges.
     */
    resetSpatialEdges() {
        this._spatialEdges = { cached: false, edges: [] };
        this._spatialEdgesChanged$.next(this._spatialEdges);
    }
    _disposeImage() {
        if (this._image != null) {
            window.URL.revokeObjectURL(this._image.src);
        }
        this._image = null;
    }
    _getThumbUrl(nodeUrls, size) {
        switch (size) {
            case ImageSize.Size320:
                return nodeUrls.thumb320_url;
            case ImageSize.Size640:
                return nodeUrls.thumb640_url;
            case ImageSize.Size1024:
                return nodeUrls.thumb1024_url;
            default:
                return nodeUrls.thumb2048_url;
        }
    }
}
//# sourceMappingURL=NodeCache.js.map